import React, { useEffect, useMemo, useState } from "react";
import styles from "./main-map-page.module.scss";
import { MapLeftPanel } from "../../shared/map-left-panel/map-left-panel";
import MapControls from "../../components/map-controls/map-controls";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import {
  MapSection,
  resetAttributesModalState,
  resetImportModalState,
  setConvertedGeometry,
  setEnableDrawingTools,
  setLayersVisible,
  setSection,
  stopDrawingTools,
} from "../../services/reducers/map";
import { focusOnFeature, initializeMap } from "../../services/actions/map";
import { useAppSelector } from "../../hooks/useAppSelector";
import { MapWrapper } from "../../shared/map-wrapper/map-wrapper";
import EditAttributesModalGlobal from "../../shared/edit-attributes-modal-global/edit-attributes-modal-global";
import { LayersDataTypes } from "../../types/layers-data-types";
import { handleSelectCard } from "../../utils/cardNavigation";

export const MainMapPage = () => {
  const [leftPanelOpened, setLeftPanelOpened] = useState<boolean>(true);
  const [panelWidth, setPanelWidth] = useState<number>(0);

  const {
    mapMode,
    attributesModal,
    drawingToolsIsActive,
    enableDrawingTools,
    mapComponentReady,
    layersIds,
    visibleLayersId,
  } = useAppSelector((state) => state.map);
  const { spacesForRent, investmentOpportunities, plots } = useAppSelector(
    (state) => state.filters,
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const mainSection = location.pathname.split("/")[1];

  const dispatch = useAppDispatch();

  const featuresByFidMap = useMemo(() => {
    const allData = [
      ...(plots.data || []),
      ...(investmentOpportunities.data || []),
      ...(spacesForRent.data || []),
    ];
    return new Map(allData.map((item) => [item.fid, item]));
  }, [plots.data, investmentOpportunities.data, spacesForRent.data]);

  useEffect(() => {
    dispatch(setSection(MapSection.search));

    if (drawingToolsIsActive) {
      dispatch(stopDrawingTools());
    }

    if (enableDrawingTools) {
      dispatch(setEnableDrawingTools(false));
    }

    return () => {
      dispatch(initializeMap({ section: MapSection.none, mode: mapMode }));
      dispatch(setConvertedGeometry(null));
      dispatch(resetImportModalState());
      dispatch(resetAttributesModalState());
    };
  }, []);

  useEffect(() => {
    dispatch(initializeMap({ section: MapSection.search, mode: mapMode }));
  }, [mapMode]);

  useEffect(() => {
    if (mapComponentReady === MapSection.search) {
      const cityBoundaryLayerIds =
        layersIds[LayersDataTypes.CityBoundary] || [];

      if (cityBoundaryLayerIds.length > 0) {
        const newVisibleLayers = [
          ...visibleLayersId,
          ...cityBoundaryLayerIds.filter((id) => !visibleLayersId.includes(id)),
        ];

        if (newVisibleLayers.length > visibleLayersId.length) {
          dispatch(setLayersVisible(newVisibleLayers));
        }
      }
    }
  }, [mapComponentReady, layersIds, mainSection]);

  useEffect(() => {
    const fid = searchParams.get("fid");
    const fromQr = searchParams.get("fromQr");

    if (!fid || !fromQr || mapComponentReady !== "search") {
      return;
    }

    const item = featuresByFidMap.get(fid);
    if (!item) return;

    dispatch(focusOnFeature(item));
    // The Krookie QR code should behave like "Show on map" + "Show
    // details" together — also open the plot's own info card, not just
    // pan/zoom to it. Always "Plots" here: this fid/fromQr entry point is
    // exclusively used by the plot location QR.
    handleSelectCard(dispatch, "Plots", fid, true);

    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("fromQr");
        return newParams;
      },
      { replace: true },
    );
  }, [featuresByFidMap, mapComponentReady, searchParams.toString()]);

  return (
    <div className={styles.mainMapPage}>
      <MapLeftPanel
        onWidthChange={setPanelWidth}
        leftPanelOpened={leftPanelOpened}
        closeLeftPanel={setLeftPanelOpened}
      >
        <Outlet />
      </MapLeftPanel>
      <MapWrapper
        leftPanelOpened={leftPanelOpened}
        panelWidth={panelWidth}
        containerId={`${MapSection.search}MapContainer`}
      />
      <MapControls />
      {attributesModal.visible && <EditAttributesModalGlobal />}
    </div>
  );
};
